import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Button, Grid, Image, ResponsiveContext, Stack, Text } from "grommet";
import * as React from "react";
import { Account, usePermission, useUser } from "./account/AccountStore";
import { AliasViewModel, getAliasesForUser } from "./aliases/AliasStore";
import CreateOwnAlias from "./aliases/CreateOwnAlias";
import SkeletonWrapper from "./components/SkeletonWrapper";
import DiscordAliasLink from "./discord/DiscordAliasLink";

import { Link } from "react-router-dom";
import useLocalStorage from "./general/LocalStorage";
import { GetCurrentLoggedInUser } from "./organizations/IdentityAPI";
import GamesView from "./public/GamesView";
import UserAliasCard from "./userExperience/UserAliasCard";
import UserHome from "./userExperience/UserHome";

const twitter = <FontAwesomeIcon size="lg" icon={["fab", "twitter"]}></FontAwesomeIcon>;
const facebook = <FontAwesomeIcon size="lg" icon={["fab", "facebook"]}></FontAwesomeIcon>;
const web = <FontAwesomeIcon size="lg" icon={["fad", "globe"]}></FontAwesomeIcon>;

interface GameShowcase {
    title: string;
    image: string;
    screenshots: string[];
    videos: string[];
    social: { link: string; icon: JSX.Element }[];
}

interface SignInButtonProps {
    loading: boolean;
    hasManagementPermission: boolean;
    user: Account;
}

const SignInButton = ({ loading, hasManagementPermission, user }: SignInButtonProps) => {
    return (
        <Box align="center" justify="center" pad="medium" width="100%">
            <SkeletonWrapper loading={loading}>
                {!hasManagementPermission && (
                    <Button
                        primary
                        size="large"
                        href={`/account/signin`}
                        onClick={(e) => {
                            e.preventDefault();
                            window.location = `/account/signin?select=true&redirectUrl=${encodeURIComponent(window.location.pathname)}` as any;
                        }}
                        label={user?.organization?.name || "Sign In"}
                    />
                )}
            </SkeletonWrapper>
        </Box>
    );
};

const Home = () => {
    const user = useUser();

    const [loading, setLoading] = React.useState(true);
    const [aliases, setAliases] = React.useState([] as AliasViewModel[]);
    const [showAliasCreate, setShowAliasCreate] = React.useState(false);
    const [discordAuthPresent, setDiscordAuthPresent] = React.useState(false);

    const getAliases = () => {
        return getAliasesForUser(user.id).then((aliases) => {
            setAliases(aliases);
            setLoading(false);
            setShowAliasCreate(aliases.length === 0);
            if (document.cookie.includes("discord_oauth")) {
                setDiscordAuthPresent(true);
            }
        });
    };

    React.useEffect(() => {
        if (user.resolved) {
            if (user.id) {
                getAliases();
            } else {
                GetCurrentLoggedInUser()
                    .then((res) => {
                        if (!user.isAuthenticated && res.data.id) {
                            window.location.assign("/account/signin");
                        } else {
                            setLoading(false);
                        }
                    })
                    .catch(() => {
                        setLoading(false);
                    });
            }
        }
    }, [user]);

    const hasManagementPermission = usePermission("sport:management");

    const size = React.useContext(ResponsiveContext);

    const [skip, setSkip] = useLocalStorage("skipCreateAlias", false);

    const isSmall = size === "small";

    return (
        <div>
            <Stack>
                {!user.isAuthenticated && (
                    <Box pad={isSmall ? "medium" : "large"} align="center" animation="fadeIn">
                        <Box direction="row" align="center" justify="center" pad={{ bottom: "medium" }} wrap>
                            <Image
                                src="/images/rgb-icon.png"
                                width={isSmall ? "50px" : "75px"}
                                height={isSmall ? "50px" : "75px"}
                                style={{
                                    padding: isSmall ? "0 1rem 0 0" : "0 2rem 0 0",
                                    margin: isSmall ? "0 1rem 0 0" : "0 2rem 0 0",
                                    borderRight: "2px solid white",
                                }}
                            ></Image>
                            <Text style={{ fontFamily: "Calamity", fontSize: isSmall ? "2rem" : "4rem" }}>GAMING</Text>
                        </Box>

                        <Text textAlign="center" size={isSmall ? "small" : "medium"}>
                            Full ESports Management and Development
                        </Text>
                        <Text size="small">
                            Visit us at{" "}
                            <Anchor href="https://www.rgbgaming.co.za/" target="_blank">
                                {isSmall ? "rgbgaming.co.za" : "https://www.rgbgaming.co.za/"}
                            </Anchor>
                        </Text>
                    </Box>
                )}
                {user.isAuthenticated && (
                    <Box pad={isSmall ? "small" : "medium"} align="center" animation="fadeIn">
                        <Box direction="row" align="center" justify="center" pad={{ bottom: isSmall ? "small" : "medium" }}>
                            <Image
                                src="/images/rgb-icon.png"
                                width={isSmall ? "35px" : "50px"}
                                height={isSmall ? "35px" : "50px"}
                                style={{
                                    padding: isSmall ? "0 0.75rem 0 0" : "0 2rem 0 0",
                                    margin: isSmall ? "0 0.75rem 0 0" : "0 2rem 0 0",
                                    borderRight: "2px solid white",
                                }}
                            ></Image>
                            <Text style={{ fontFamily: "Calamity", fontSize: isSmall ? "1.75rem" : "3rem" }}>GAMING</Text>
                        </Box>
                    </Box>
                )}
                {!isSmall && <SignInButton loading={loading} hasManagementPermission={hasManagementPermission} user={user} />}
            </Stack>
            {isSmall && <SignInButton loading={loading} hasManagementPermission={hasManagementPermission} user={user} />}
            {!loading && (
                <Box>
                    <Box animation="fadeIn">
                        {showAliasCreate && !discordAuthPresent && (
                            <Box align="center" background={"glass"} pad={{ vertical: "small", horizontal: isSmall ? "small" : "medium" }}>
                                {skip && <Anchor onClick={() => setSkip(false)} label="Create your alias"></Anchor>}
                                {!skip && (
                                    <>
                                        <Text size={isSmall ? "medium" : "large"}>Create your alias</Text>
                                        <Anchor onClick={() => setSkip(true)} label="Not now" size="small"></Anchor>
                                        {isSmall ? (
                                            <Box gap="medium" align="center" width="100%" pad={{ horizontal: "small" }}>
                                                <Box width="100%">
                                                    <CreateOwnAlias onCreated={() => getAliases()}></CreateOwnAlias>
                                                </Box>
                                                <Text size="medium">OR</Text>
                                                <Box width="100%">
                                                    <DiscordAliasLink aliasID={""}></DiscordAliasLink>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Grid columns={{ count: 3, size: "auto" }} width={{ max: "900px" }}>
                                                <Box width={{ max: "330px" }}>
                                                    <CreateOwnAlias onCreated={() => getAliases()}></CreateOwnAlias>
                                                </Box>
                                                <Box align="center" justify="center">
                                                    <Text size="medium">OR</Text>
                                                </Box>
                                                <DiscordAliasLink aliasID={""}></DiscordAliasLink>
                                            </Grid>
                                        )}
                                    </>
                                )}
                            </Box>
                        )}
                        <Box align="center" direction={isSmall ? "column" : "row"} justify="center" gap={isSmall ? "medium" : "small"} wrap>
                            {aliases?.length > 0 &&
                                aliases.map((alias) => (
                                    <UserAliasCard key={alias.id} {...alias}>
                                        <Box align="center" justify="center" gap="small" pad="small">
                                            {alias.discordID && (
                                                <Link to="/discord">
                                                    <Box pad="small" background="#000000AA" round="5px">
                                                        <Image src={"/images/discord-logo-blue.png"} width={isSmall ? "80px" : "100px"}></Image>
                                                    </Box>
                                                </Link>
                                            )}
                                        </Box>
                                    </UserAliasCard>
                                ))}
                        </Box>
                        {user.isAuthenticated && (
                            <Box pad={isSmall ? "xsmall" : "small"}>
                                <UserHome></UserHome>
                            </Box>
                        )}
                        {!user.isAuthenticated && <GamesView></GamesView>}
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default Home;
